import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Lock, Unlock } from 'lucide-react';
import { biz } from '../../lib/api';
import { Card } from './BusinessLayout';

export default function DayClose() {
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [summary, setSummary] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setSummary(await biz.daySummary(day)); }
    catch (e) { setSummary(null); }
    try { setHistory(await biz.listDayClosings()); } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeDay = async () => {
    if (!window.confirm(`Close day ${day}? Editing will be locked for regular staff.`)) return;
    setBusy(true);
    try { await biz.dayClose(day, remarks); toast.success('Day closed'); setRemarks(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const reopenDay = async (d) => {
    if (!window.confirm(`Reopen ${d}?`)) return;
    try { await biz.dayReopen(d); toast.success('Day reopened'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const activeClose = history.find((h) => h.day === day && !h.reopened_at);

  return (
    <div className="grid gap-3" data-testid="biz-dayclose-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-black text-[#0D47A1]">Day Closing</div>
        <h1 className="font-heading font-black text-2xl md:text-3xl">Close Business Day</h1>
      </div>

      <div className="card-soft p-3 flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1 text-xs uppercase tracking-widest text-ink/50">
          Day
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
            className="rounded-lg border border-border px-2 py-1.5 bg-white"
            data-testid="biz-dayclose-day"/>
        </label>
        <label className="flex flex-col gap-1 flex-1 text-xs uppercase tracking-widest text-ink/50">
          Remarks (optional)
          <input value={remarks} onChange={(e) => setRemarks(e.target.value)}
            className="rounded-lg border border-border px-2 py-1.5 bg-white"
            data-testid="biz-dayclose-remarks"/>
        </label>
        {activeClose ? (
          <button className="btn-primary !bg-[#1B5E20] !border-[#1B5E20]" onClick={() => reopenDay(day)}
            data-testid="biz-dayclose-reopen-btn">
            <Unlock className="w-4 h-4"/>Reopen day
          </button>
        ) : (
          <button className="btn-primary !bg-[#B71C1C] !border-[#B71C1C]" disabled={busy} onClick={closeDay}
            data-testid="biz-dayclose-close-btn">
            <Lock className="w-4 h-4"/>Close day
          </button>
        )}
      </div>

      {activeClose && (
        <div className="rounded-2xl border-2 border-[#B71C1C]/40 bg-[#FFEBEE]/40 p-3 text-sm"
          data-testid="biz-dayclose-locked-note">
          Day is <b>closed</b> by <b>{activeClose.closed_by_name}</b> at{' '}
          {new Date(activeClose.closed_at).toLocaleString()}. Only admin can reopen.
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <Card testid="biz-dc-total" title="Total activities" value={summary.total_activities}/>
          <Card testid="biz-dc-completed" title="Completed" value={summary.completed} tone="good"/>
          <Card testid="biz-dc-pending" title="Pending" value={summary.pending} tone="warn"/>
          <Card testid="biz-dc-collections" title="Collections (₹)" value={money(summary.collections_inr)} tone="good"/>
          <Card testid="biz-dc-expenses" title="Expenses (₹)" value={money(summary.expenses_inr)} tone="warn"/>
          <Card testid="biz-dc-new-complaints" title="New complaints" value={summary.new_complaints}/>
          <Card testid="biz-dc-resolved" title="Resolved today" value={summary.resolved_complaints_today} tone="good"/>
          <Card testid="biz-dc-tomorrow-fu" title="Tomorrow follow-ups" value={summary.tomorrow_followups}/>
        </div>
      )}

      <section className="card-soft p-3">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink/60 mb-2">Recent closings</div>
        <div className="grid gap-1">
          {history.slice(0, 20).map((h) => (
            <div key={h.id} className="border border-border rounded-lg px-2 py-1.5 flex items-center gap-2 text-sm"
              data-testid={`biz-dayclose-history-${h.day}`}>
              <span className="font-mono">{h.day}</span>
              <span className="text-xs text-ink/60">by {h.closed_by_name} at {new Date(h.closed_at).toLocaleString()}</span>
              {h.reopened_at && <span className="text-[10px] uppercase tracking-widest text-[#EF6C00]">Reopened at {new Date(h.reopened_at).toLocaleString()}</span>}
              {!h.reopened_at && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#B71C1C]">Closed</span>}
            </div>
          ))}
          {!history.length && <div className="text-xs text-ink/50">No day-closings yet.</div>}
        </div>
      </section>
    </div>
  );
}

function money(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
